import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '@/stores/session';
import { hasPermission, type PermissionBit } from '@/lib/shared';
import { keys } from '@/lib/api/keys';
import type { Asset } from '@/lib/api/types';

/**
 * §6.4 — effective mask resolution order: asset.effective_mask (cached asset query) →
 * board effective_mask remembered from board/asset responses → principal mask (role / share / guest).
 */
export function useEffectiveMask(scope?: { boardId?: string | null; assetId?: string | null }): number {
  const principal = useSessionStore((s) => s.principal);
  const boardMask = useSessionStore((s) => (scope?.boardId ? s.boardMasks[scope.boardId] : undefined));
  const qc = useQueryClient();
  if (scope?.assetId) {
    const a = qc.getQueryData<Asset>(keys.asset(scope.assetId));
    if (a?.effective_mask != null) return Number(a.effective_mask);
  }
  if (boardMask != null) return boardMask;
  return principal.mask;
}

export function usePermission(bit: PermissionBit, scope?: { boardId?: string | null; assetId?: string | null }): boolean {
  const mask = useEffectiveMask(scope);
  return hasPermission(mask, bit);
}

export function Can({ bit, boardId, assetId, children, fallback = null }: { bit: PermissionBit; boardId?: string | null; assetId?: string | null; children: ReactNode; fallback?: ReactNode }) {
  const ok = usePermission(bit, { boardId, assetId });
  return <>{ok ? children : fallback}</>;
}
